import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Square } from "lucide-react";
import {
  CameraError,
  CelebrityCircles,
  CompetitiveHud,
  DepthBar,
  FinishedOverlay,
  RecordButton,
  RecordedClipOverlay,
  ROUND_END_RECORDING_TAIL_MS,
  VideoStage,
  VideoStageLoader,
  WorkoutConsole,
  usePoseDetection,
  useRecordingComposite,
  useVoicePhases,
  type ClipRecordingResult,
} from "@/features/workout";
import { CelebrityPicker, useSelectedCelebrity } from "@/features/celebrity";
import { PublicPreviewButton } from "@/features/public-videos";
import { authClient } from "@/lib/auth";
import { useCelebrityStore } from "@/state/celebrity-store";
import { useWorkoutStore } from "@/state/workout-store";

/**
 * The main workout screen (rendered at "/"). Two ways in:
 *  - tapping the idle screen starts a plain training session with the
 *    currently selected opponent, and
 *  - tapping the record circle starts a recording session (the camera boots,
 *    the whole workout is captured).
 * During training the only control is the centered Give Up button; giving up
 * after a recording session replays the clip full-screen with download /
 * publish / share actions.
 */
export function WorkoutPage() {
  const { data: session } = authClient.useSession();
  const signedIn = Boolean(session?.session);
  const status = useWorkoutStore((s) => s.status);
  const errorMessage = useWorkoutStore((s) => s.errorMessage);
  const reps = useWorkoutStore((s) => s.reps);
  const elapsedMs = useWorkoutStore((s) => s.elapsedMs);
  const pushupsStarted = useWorkoutStore((s) => s.pushupsStarted);
  const startCelebrityPacing = useWorkoutStore((s) => s.startCelebrityPacing);
  const stopCelebrityPacing = useWorkoutStore((s) => s.stopCelebrityPacing);
  const finishWorkout = useWorkoutStore((s) => s.finishWorkout);
  const selectCelebrity = useCelebrityStore((s) => s.selectCelebrity);
  const navigate = useNavigate();

  const selectedCelebrity = useSelectedCelebrity();

  const [showPicker, setShowPicker] = useState(false);
  const [recordedClip, setRecordedClip] = useState<ClipRecordingResult | null>(null);
  const [recordingSession, setRecordingSession] = useState(false);
  const [roundEnded, setRoundEnded] = useState(false);
  const roundEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRoundEndTimer = useCallback(() => {
    if (roundEndTimerRef.current !== null) {
      clearTimeout(roundEndTimerRef.current);
      roundEndTimerRef.current = null;
    }
  }, []);

  const { videoRef, canvasRef, start, stop, getStream, getLastLandmarks } = usePoseDetection({
    mode: "full",
    audio: true,
  });

  const {
    isRecording,
    recordingSeconds,
    result,
    start: startRecording,
    stop: stopRecording,
    reset: resetRecording,
    supported,
  } = useRecordingComposite({
    getVideo: () => videoRef.current,
    getLandmarks: () => getLastLandmarks(),
    getCelebrity: () => selectedCelebrity,
    getRoundEnded: () => roundEnded,
  });

  // Idle controls (opponent circles, record, public feed) only show before the
  // camera is running; the centered Give Up takes over during training.
  const isIdle = status === "idle";

  // Voice phase audio playback during active workout — starts with the user's
  // first pushup, alongside the timer and the opponent's pacing.
  useVoicePhases({
    phases: selectedCelebrity?.voicePhases ?? [],
    enabled: status === "active" && pushupsStarted,
  });

  // The celebrity opponent starts doing reps only once the user begins their
  // first pushup (elbow bends into the first rep) — not the moment the camera
  // and model are ready.
  useEffect(() => {
    if (status !== "active" || !selectedCelebrity || !pushupsStarted) {
      return;
    }
    startCelebrityPacing(selectedCelebrity.secondsPerRep);
  }, [status, pushupsStarted, selectedCelebrity, startCelebrityPacing]);

  // Keep the finished clip from the recorder (also fed in by handleFinish).
  useEffect(() => {
    if (result) {
      setRecordedClip(result);
      resetRecording();
    }
  }, [result, resetRecording]);

  // End of session: stop recording (awaiting the clip if one is being taken),
  // then stop the detection engine and show the end screen.
  const handleFinish = useCallback(async () => {
    clearRoundEndTimer();
    stopCelebrityPacing();
    if (isRecording) {
      const clip = await stopRecording();
      if (clip) {
        setRecordedClip(clip);
      }
    }
    stop({ keepStatus: true });
    finishWorkout();
  }, [clearRoundEndTimer, stop, stopCelebrityPacing, finishWorkout, isRecording, stopRecording]);

  // Per-celebrity round length: when the clock hits zero, freeze the opponent
  // but keep recording for a short tail so the final reps past the bell are
  // captured. Non-recording sessions finish right away.
  const roundDurationSec = selectedCelebrity?.workoutDurationSec ?? 0;
  useEffect(() => {
    if (status !== "active" || roundDurationSec <= 0 || roundEnded) {
      return;
    }
    if (elapsedMs < roundDurationSec * 1000) {
      return;
    }
    setRoundEnded(true);
    stopCelebrityPacing();
    if (recordingSession) {
      roundEndTimerRef.current = setTimeout(() => {
        roundEndTimerRef.current = null;
        void handleFinish();
      }, ROUND_END_RECORDING_TAIL_MS);
    } else {
      void handleFinish();
    }
  }, [
    status,
    roundDurationSec,
    elapsedMs,
    roundEnded,
    recordingSession,
    stopCelebrityPacing,
    handleFinish,
  ]);

  // Clear the round-end marker (and any pending tail timer) once a session
  // ends or a new one is being set up.
  useEffect(() => {
    if (status !== "active") {
      setRoundEnded(false);
      clearRoundEndTimer();
    }
  }, [status, clearRoundEndTimer]);

  // Never leave a pending round-end timer running on unmount.
  useEffect(() => clearRoundEndTimer, [clearRoundEndTimer]);

  // Tap anywhere on the idle screen: start training with the selected opponent.
  const handleStartClicked = useCallback(() => {
    void start();
  }, [start]);

  // Record clip: on the idle screen this also boots the camera and starts the
  // session. During a recording session the record button IS the give-up — it
  // stops the recording and ends the session so the clip can be replayed.
  const handleRecordClick = useCallback(async () => {
    if (isRecording) {
      void handleFinish();
      return;
    }
    let stream = getStream();
    if (!stream) {
      await start();
      stream = getStream();
      if (!stream) {
        return;
      }
    }
    setRecordingSession(true);
    startRecording(stream);
  }, [isRecording, getStream, start, startRecording, handleFinish]);

  // Celebrity mode requires an account (the full roster with coaching data is
  // only served to signed-in users). A signed-out tap on a circle (or "All")
  // sends the visitor to the login page; signed-in taps select normally.
  const requireSignedIn = useCallback(() => {
    if (signedIn) {
      return true;
    }
    void navigate({ to: "/login", search: { redirect: "/" } });
    return false;
  }, [signedIn, navigate]);

  // Tapping a celebrity circle (or the picker) just selects the opponent —
  // training starts separately by tapping the screen. Signed-out users are
  // routed to the login page first.
  const handleCircleSelect = useCallback(
    (celebrityId: string) => {
      if (!requireSignedIn()) return;
      selectCelebrity(celebrityId);
    },
    [requireSignedIn, selectCelebrity],
  );

  const handleBrowseAll = useCallback(() => {
    if (!requireSignedIn()) return;
    setShowPicker(true);
  }, [requireSignedIn]);

  // Fully end the session: camera off, back to the idle screen.
  const handleStop = useCallback(() => {
    stopCelebrityPacing();
    resetRecording();
    setRecordedClip(null);
    setRecordingSession(false);
    stop();
  }, [stop, stopCelebrityPacing, resetRecording]);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black text-white">
      {/* Full-screen camera feed + skeleton overlay */}
      <VideoStage videoRef={videoRef} canvasRef={canvasRef} onStart={handleStartClicked} />
      {status === "loading" && <VideoStageLoader />}

      {/* Opponent quick-select, bottom-left corner, idle only. The circles load
          the public roster preview so they show for everyone; signed-out taps
          are routed to the login page by handleCircleSelect/handleBrowseAll. */}
      {isIdle && (
        <div className="absolute bottom-5 left-3 z-10">
          <CelebrityCircles onBrowseAll={handleBrowseAll} onSelect={handleCircleSelect} />
        </div>
      )}

      {/* Top HUD — scoreboard with the round timer inside. Narrower during a
          plain (non-recording) training session so the camera preview stays
          prominent; full-width during a recording session. */}
      <CompetitiveHud recording={recordingSession} roundEnded={roundEnded} />

      {/* Vertical depth meter */}
      <DepthBar className="absolute right-1.5 top-1/2 h-[58vh] -translate-y-1/2" />

      {/* Give Up — the only control during a plain (non-recording) training
          session; the record/stop button acts as give up when recording. */}
      {status === "active" && !recordingSession && (
        <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2">
          <button
            type="button"
            onClick={() => void handleFinish()}
            className="flex items-center gap-2.5 rounded-xl bg-red-600 px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-white shadow-[0_8px_24px_rgba(220,38,38,0.35)] transition-all hover:bg-red-500"
          >
            <Square className="size-4 fill-current" />
            Give Up
          </button>
        </div>
      )}

      {/* Record circle: idle bottom-center starts a recording session; once one
          is running it stays centered and shows the recording state (the stop
          button acts as give up). */}
      {supported && isIdle && (
        <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2">
          <RecordButton
            isRecording={false}
            seconds={0}
            captured={false}
            onClick={() => void handleRecordClick()}
          />
        </div>
      )}
      {supported && status === "active" && recordingSession && (
        <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2">
          <RecordButton
            isRecording={isRecording}
            seconds={recordingSeconds}
            captured={recordedClip !== null}
            onClick={() => void handleRecordClick()}
          />
        </div>
      )}

      {/* Public feed shortcut — bottom right, idle only. The feed is behind the
          sign-in wall, so the shortcut only appears for signed-in users. */}
      {isIdle && signedIn && (
        <div className="absolute bottom-5 right-5 z-10">
          <PublicPreviewButton />
        </div>
      )}

      {/* Session info + form feedback */}
      <WorkoutConsole />

      {/* End-of-session screens: replay a recorded clip full-screen, otherwise
          show the round results. */}
      {status === "finished" && recordedClip && (
        <RecordedClipOverlay
          clip={recordedClip}
          reps={reps}
          celebrityName={selectedCelebrity?.name}
          onFinish={handleStop}
        />
      )}
      {status === "finished" && !recordedClip && (
        <FinishedOverlay reps={reps} onFinish={handleStop} />
      )}

      {/* Opponent Selection Dialog (selects only — start via the screen tap) */}
      <CelebrityPicker
        open={showPicker && signedIn}
        onClose={() => setShowPicker(false)}
        onConfirm={() => setShowPicker(false)}
      />

      {errorMessage && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/80 p-6">
          <CameraError message={errorMessage} onRetry={handleStartClicked} />
        </div>
      )}
    </div>
  );
}
